import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Brain, Sprout, ArrowRight, ShieldCheck } from 'lucide-react';
import PortalShell from '@/components/portal/PortalShell';
import PublicHero from '@/components/portal/PublicHero';

// WP2.1B-R2: rebuilt to the LOCKED About composition (giisdecsite_30 · about).
// Presentation-only, frozen EDS v1.0 tokens. Unverified figures are rendered
// under the governed Option B posture ("Awaiting Validation") — no fabricated data.

const PILLARS = [
  {
    icon: <BookOpen className="w-6 h-6" />,
    title: 'INFORM',
    body: 'Deliver approved geological information and transparent public data through a single trusted source.',
  },
  {
    icon: <Brain className="w-6 h-6" />,
    title: 'ANALYZE',
    body: 'Turn survey data and GIS layers into actionable mineral resource intelligence.',
  },
  {
    icon: <Sprout className="w-6 h-6" />,
    title: 'SUSTAIN',
    body: 'Enable responsible investment and sustainable development of critical minerals.',
  },
];

// Governed statistics (Option B). Data-backed counts remain "Awaiting Validation"
// until NITA-verified; operational descriptors are shown as-is.
const STATS = [
  { value: 'Awaiting Validation', label: 'Mapped Concessions', governed: true },
  { value: 'Awaiting Validation', label: 'Critical Minerals Tracked', governed: true },
  { value: '100%', label: 'Digital Process', governed: false },
  { value: 'Role-Based', label: 'Secure Access', governed: false },
];

const MANDATE = [
  { title: 'Geological Information Mgmt', tag: 'INFORM' },
  { title: 'GIS & Spatial Intelligence', tag: 'ANALYZE' },
  { title: 'Investor Services', tag: 'SUSTAIN' },
  { title: 'Regulatory Governance', tag: 'GOVERN' },
  { title: 'Executive Oversight', tag: 'LEAD' },
];

const About: React.FC = () => {
  return (
    <PortalShell>
      <PublicHero
        badge="About GIISDEC"
        title={
          <>
            <span className="text-white">Inform</span>
            <span className="text-white/40"> · </span>
            <span className="text-eds-mint">Analyze</span>
            <span className="text-white/40"> · </span>
            <span className="text-eds-emerald-2">Sustain</span>
          </>
        }
        subtitle="The Geo-Information System for Sustainable Development and Exploration of Critical Minerals, unifying Ghana's geological data, GIS intelligence, investor services and regulatory governance."
      />

      {/* Pillars */}
      <section className="py-16 md:py-20 bg-eds-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          {PILLARS.map((p) => (
            <div key={p.title} className="text-center">
              <div className="mx-auto w-14 h-14 rounded-eds-lg bg-eds-surface-2 border border-eds-border flex items-center justify-center text-eds-emerald-2">
                {p.icon}
              </div>
              <h3 className="font-eds-display text-lg font-bold text-eds-fg mt-5 tracking-wide">{p.title}</h3>
              <p className="text-sm text-eds-muted-fg mt-2 max-w-xs mx-auto leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Governed statistics band */}
      <section className="pb-4 bg-eds-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-eds-lg bg-eds-surface border border-eds-border shadow-eds-sm grid grid-cols-2 lg:grid-cols-4 divide-x divide-eds-border">
            {STATS.map((s) => (
              <div key={s.label} className="px-6 py-8 text-center">
                <div
                  className={
                    s.governed
                      ? 'font-eds-display text-base font-semibold text-eds-emerald-2'
                      : 'font-eds-display text-3xl font-bold text-eds-fg'
                  }
                >
                  {s.value}
                </div>
                <div className="text-xs text-eds-muted-fg mt-1.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Mandate split */}
      <section className="py-16 md:py-20 bg-eds-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-eds-emerald-2">Our Mandate</p>
            <h2 className="font-eds-display text-3xl md:text-4xl font-bold text-eds-fg mt-3 tracking-[-0.01em]">
              A national platform for the mineral economy
            </h2>
            <p className="text-eds-muted-fg mt-5 leading-relaxed">
              GIISDEC brings geological survey data, mineral resource intelligence and investor
              opportunities into one secure, government-operated portal. Built on the NITA National
              Data Centre with role-based access and full audit compliance, it supports agencies,
              investors and partners across the value chain.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-eds-pill bg-eds-primary text-eds-primary-fg font-semibold text-sm hover:bg-eds-emerald-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
              >
                Get in Touch <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-eds-pill border border-eds-border text-eds-fg font-semibold text-sm hover:bg-eds-surface-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
              >
                Our Services
              </Link>
            </div>
          </div>

          <div className="rounded-eds-lg bg-eds-forest text-white p-6 md:p-8 shadow-eds-md">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-eds-mint mb-5">
              <ShieldCheck className="w-4 h-4" /> Operating pillars
            </div>
            <ul className="divide-y divide-white/10">
              {MANDATE.map((m) => (
                <li key={m.title} className="flex items-center justify-between py-4">
                  <span className="font-medium text-white/90">{m.title}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-eds-mint bg-white/10 border border-white/15 rounded-eds-pill px-2.5 py-1">
                    {m.tag}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </PortalShell>
  );
};

export default About;
